/** Where the insertion indicator sits relative to the target item. */
export type NxSortableDropSide = 'before' | 'after';

/**
 * Moves the item at `from` so that it ends up at index `to`. Returns a new
 * array; the input is never mutated. Out-of-range indices and `from === to`
 * return an unchanged copy, so callers need no guard.
 */
export function reorderItems<T>(items: readonly T[], from: number, to: number): T[] {
  const next = [...items];
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) {
    return next;
  }
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved as T);
  return next;
}

/**
 * Moves the item at `index` by `delta` positions, clamped to the ends — the
 * keyboard path. Clamping, not wrapping: holding an arrow key at the edge
 * should do nothing, not teleport the item to the other end.
 */
export function moveItemBy<T>(items: readonly T[], index: number, delta: number): T[] {
  return reorderItems(items, index, clampIndex(index + delta, items.length));
}

export function clampIndex(index: number, count: number): number {
  return Math.min(Math.max(0, count - 1), Math.max(0, index));
}

/**
 * Which side of the item now at `to` the indicator belongs on. The source is
 * spliced out before it is spliced back in, so a forward move lands AFTER the
 * target and a backward move BEFORE it; `null` for a no-op move.
 */
export function sortableDropSide(from: number, to: number): NxSortableDropSide | null {
  if (from === to || from < 0 || to < 0) return null;
  return from < to ? 'after' : 'before';
}

/** One item's extent along the drag axis (viewport coordinates). */
export type NxSortableSpan = { start: number; end: number };

/**
 * The index the dragged item at `from` would end up at when dropped with the
 * pointer at `pointer`: the number of OTHER items whose midpoint lies before
 * the pointer. `spans` are in list order and include the dragged item, which
 * is skipped (it moves with the pointer).
 */
export function sortableTargetIndex(
  spans: readonly NxSortableSpan[],
  from: number,
  pointer: number
): number {
  let slot = 0;
  spans.forEach((span, index) => {
    if (index !== from && (span.start + span.end) / 2 < pointer) slot += 1;
  });
  return slot;
}
